import { useRef } from 'react';

import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import 'swiper/css';

import { ProductType } from '@/data/types/Product';
import { Box, Typography } from '@mui/material';
import { ProductCard } from '@/components/blocks/ProductCard';

import { useVisibleSlides } from '@/components/blocks/ProductCarousel/utils/useVisibleSlides';
import { ProductCarouselNavigation } from '@/components/blocks/ProductCarousel/parts/Navigation';
import {
	rootSx,
	productCardSx,
	titleContainerSx,
	titleSX,
} from '@/components/blocks/ProductCarousel/styles/productCarousel';
import { RenderContent } from '@/components/blocks/RenderContent';
import { ProcessedContent } from '@/data/types/Marketing';

type Props = {
	products: ProductType[];
	title?: string | ProcessedContent;
	isEmarsys?: boolean;
	onClickProduct?: (product: ProductType) => void;
};

export const ProductCarousel = ({ products, title, onClickProduct, isEmarsys }: Props) => {
	const swiperRef = useRef<SwiperRef>(null);

	const { visibleSlides, lgMatch } = useVisibleSlides();
	const totalSlides = products.length;

	const renderTitle = () => {
		if (!title) {
			return <Box />;
		}
		// Validate plain text title for emarsys carousels
		if (typeof title === 'string') {
			const normalizedTitle = title.trim();
			
			return normalizedTitle ? (
				<Typography variant="body1AsH2" sx={titleSX}>
					{normalizedTitle}
				</Typography>
			) : (
				<Box />
			);
		}
		// Validate titles from espot that contains html code
		if (title.text?.trim()) {
			return (
				<RenderContent
					key={`${title.id ?? ''}${title.contentId ?? ''}`}
					content={title}
				/>
			);
		}
		return <Box />;
	};

	return (
		// eslint-disable-next-line react/forbid-component-props
		<Box sx={rootSx} className='carousel'>
			{/* eslint-disable-next-line react/forbid-component-props */}
			<Box sx={titleContainerSx} className='carousel__title'>
				{renderTitle()}
				<ProductCarouselNavigation swiperRef={swiperRef} totalSlides={totalSlides} />
			</Box>

			<Swiper ref={swiperRef} slidesPerView={visibleSlides} spaceBetween={lgMatch ? 20 : 15}>
				{products.map((product, i) => (
					// eslint-disable-next-line react/forbid-component-props
					<SwiperSlide
						key={product.partNumber}
						id={`carousel-slide-index-${i}-${product.partNumber}`}
						data-testid={`carousel-slide-index-${i}-${product.partNumber}`}
						className='carousel__slide'
					>
						<ProductCard
							product={product}
							showInventory
							productCardSx={productCardSx}
							clickAction={() => onClickProduct?.(product)}
							isEmarsys={isEmarsys}
						/>
					</SwiperSlide>
				))}
			</Swiper>
		</Box>
	);
};
